import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLanguage } from './LanguageContext';

const EditorContext = createContext();

export const useEditor = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within EditorProvider');
  }
  return context;
};

export const EditorProvider = ({ children }) => {
  const { language } = useLanguage();
  const [isEditorMode, setIsEditorMode] = useState(false);
  const [editedContent, setEditedContent] = useState({});
  const [originalContent, setOriginalContent] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load content from database when language changes
  useEffect(() => {
    if (isEditorMode && language) {
      // Clear existing content before loading new language
      setEditedContent({});
      setOriginalContent({});
      loadContentFromDatabase(language);
    }
  }, [language, isEditorMode]);

  const loadContentFromDatabase = async (lang) => {
    if (!lang) {
      console.warn('Language not set, skipping content load');
      return;
    }
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        console.error('Backend URL is not configured. Please set REACT_APP_BACKEND_URL environment variable.');
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/site-content/${lang}`);
      const data = await response.json();
      
      if (data.success && data.data) {
        const contentMap = {};
        data.data.forEach(item => {
          const key = `${item.section}.${item.field}`;
          contentMap[key] = {
            value: item.value,
            type: item.type
          };
        });
        setEditedContent(contentMap);
        setOriginalContent(contentMap);
      }
    } catch (error) {
      console.error('Error loading content from database:', error);
    }
  };

  const enableEditorMode = () => {
    setIsEditorMode(true);
    if (language) {
      loadContentFromDatabase(language);
    }
  };

  const disableEditorMode = () => {
    setIsEditorMode(false);
    setEditedContent({});
    setOriginalContent({});
    setIsDirty(false);
  };

  const updateContent = (section, field, value, type = 'text') => {
    const key = `${section}.${field}`;
    setEditedContent(prev => ({
      ...prev,
      [key]: { value, type }
    }));
    setIsDirty(true);
  };

  const getContent = (section, field, fallback = '') => {
    const key = `${section}.${field}`;
    return editedContent[key]?.value || fallback;
  };

  const saveChanges = async () => {
    if (!language) {
      return { success: false, message: 'Language not set' };
    }
    
    setIsSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        throw new Error('Backend URL is not configured. Please set REACT_APP_BACKEND_URL environment variable.');
      }
      
      // Prepare updates array
      const updates = Object.keys(editedContent).map(key => {
        const [section, field] = key.split('.');
        return {
          language,
          section,
          field,
          value: editedContent[key].value,
          type: editedContent[key].type
        };
      });

      const response = await fetch(`${backendUrl}/api/site-content/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      
      if (data.success) {
        setOriginalContent(editedContent);
        setIsDirty(false);
        return { success: true, message: 'Changes saved successfully!' };
      } else {
        throw new Error(data.message || 'Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      return { success: false, message: error.message };
    } finally {
      setIsSaving(false);
    }
  };

  const discardChanges = () => {
    setEditedContent(originalContent);
    setIsDirty(false);
  };

  const resetToDefaults = async () => {
    if (!language) {
      return { success: false, message: 'Language not set' };
    }
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        throw new Error('Backend URL is not configured. Please set REACT_APP_BACKEND_URL environment variable.');
      }
      
      const response = await fetch(`${backendUrl}/api/site-content/reset/${language}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEditedContent({});
        setOriginalContent({});
        setIsDirty(false);
        return { success: true, message: 'Content reset to defaults!' };
      } else {
        throw new Error(data.message || 'Failed to reset content');
      }
    } catch (error) {
      console.error('Error resetting content:', error);
      return { success: false, message: error.message };
    }
  };

  const value = {
    isEditorMode,
    enableEditorMode,
    disableEditorMode,
    updateContent,
    getContent,
    saveChanges,
    discardChanges,
    resetToDefaults,
    isDirty,
    isSaving,
    editedContent
  };

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
};
